import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, CheckCircle2, ExternalLink, Eye, EyeOff, RefreshCw, Wand2 } from 'lucide-react'
import {
  Badge,
  Button,
  Card,
  DataTable,
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownTrigger,
  EmptyState,
  IconButton,
  StatusBadge,
  textColumn,
  useToast,
  type ColumnDef,
} from '@/components/ui'
import {
  useFindings,
  useReconciliationSummary,
  useTriggerReconciliation,
  useUpdateFinding,
} from '../api/reconciliationApi.ts'
import type {
  FindingSeverity,
  FindingStatus,
  FindingType,
  ReconciliationFinding,
  ReconciliationSource,
} from '../types.ts'

const SOURCE_LABELS: Record<ReconciliationSource, string> = {
  also: 'ALSO',
  eset: 'ESET',
  tanss: 'TANSS',
  cwa: 'CWA',
  m365: 'Microsoft 365',
  sap: 'SAP',
  manual: 'Manuell',
}

const FINDING_TYPE_LABELS: Record<FindingType, string> = {
  qty_mismatch: 'Mengen-Abweichung',
  missing_in_external: 'Fehlt im Umsystem',
  missing_in_contract: 'Nicht im Vertrag',
  license_expired: 'Lizenz abgelaufen',
  license_expiring: 'Lizenz läuft bald ab',
  asset_offline: 'Asset offline',
  other: 'Sonstiges',
}

function severityVariant(s: FindingSeverity): 'info' | 'warning' | 'danger' {
  if (s === 'critical') return 'danger'
  if (s === 'warning') return 'warning'
  return 'info'
}

function fmtDelta(d: number | null): string {
  if (d == null) return '—'
  const v = d / 1000
  return (v > 0 ? '+' : '') + v.toLocaleString('de-DE', { maximumFractionDigits: 3 })
}

export function SollIstSection() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const [statusFilter, setStatusFilter] = useState<FindingStatus | 'all'>('open')
  const summary = useReconciliationSummary()
  const findings = useFindings(statusFilter === 'all' ? {} : { status: statusFilter })
  const trigger = useTriggerReconciliation()
  const updateFinding = useUpdateFinding()

  const availableSources = summary.data?.availableSources ?? []
  const lastRun = summary.data?.lastRun
  const open = summary.data?.openFindings ?? 0
  const critical = summary.data?.criticalFindings ?? 0

  function handleTrigger(source: ReconciliationSource) {
    trigger.mutate(source, {
      onSuccess: (r) => {
        if (r.status === 'error') {
          toast({
            title: `${SOURCE_LABELS[source]}-Lauf fehlgeschlagen`,
            description: r.errorMessage,
            tone: 'danger',
          })
        } else {
          toast({
            title: `${SOURCE_LABELS[source]}-Lauf abgeschlossen`,
            description: `${r.findingsCount} Finding(s)`,
            tone: r.status === 'partial' ? 'warning' : 'success',
          })
        }
      },
      onError: (err) =>
        toast({
          title: 'Lauf fehlgeschlagen',
          description: err instanceof Error ? err.message : 'Unbekannter Fehler',
          tone: 'danger',
        }),
    })
  }

  function setFindingStatus(f: ReconciliationFinding, next: FindingStatus) {
    updateFinding.mutate(
      { id: f.id, status: next },
      {
        onSuccess: () => toast({ title: 'Finding aktualisiert', tone: 'success' }),
        onError: (err) =>
          toast({
            title: 'Aktualisierung fehlgeschlagen',
            description: err instanceof Error ? err.message : 'Unbekannter Fehler',
            tone: 'danger',
          }),
      },
    )
  }

  const columns = useMemo<ColumnDef<ReconciliationFinding, unknown>[]>(
    () => [
      {
        accessorKey: 'severity',
        header: '',
        size: 50,
        cell: ({ getValue }) => {
          const s = getValue() as FindingSeverity
          return (
            <Badge variant={severityVariant(s)} size="sm">
              {s === 'critical' ? '!!' : s === 'warning' ? '!' : 'i'}
            </Badge>
          )
        },
      },
      {
        accessorKey: 'source',
        header: 'Quelle',
        size: 100,
        cell: ({ getValue }) => (
          <Badge variant="info" size="sm">{SOURCE_LABELS[getValue() as ReconciliationSource]}</Badge>
        ),
      },
      {
        accessorKey: 'findingType',
        header: 'Typ',
        size: 180,
        cell: ({ getValue }) => (
          <span className="text-small text-gray-700">
            {FINDING_TYPE_LABELS[getValue() as FindingType] ?? String(getValue())}
          </span>
        ),
      },
      {
        accessorKey: 'contractNumber',
        header: 'Vertrag',
        size: 130,
        cell: ({ row }) =>
          row.original.contractId ? (
            <button
              type="button"
              className="inline-flex items-center gap-1 text-brand-700 hover:underline"
              onClick={(e) => {
                e.stopPropagation()
                navigate(`/vertraege/${row.original.contractId}`)
              }}
            >
              <ExternalLink className="w-3 h-3" />
              {row.original.contractNumber ?? '—'}
            </button>
          ) : (
            <span className="text-caption text-gray-400">—</span>
          ),
      },
      textColumn<ReconciliationFinding>('description', 'Beschreibung'),
      {
        id: 'delta',
        header: 'Soll → Ist',
        size: 130,
        cell: ({ row }) => {
          const f = row.original
          if (f.sollQuantityX1000 == null && f.istQuantityX1000 == null) {
            return <span className="text-caption text-gray-400">—</span>
          }
          return (
            <span className="text-small tabular-nums">
              {f.sollQuantityX1000 != null ? f.sollQuantityX1000 / 1000 : '—'}
              {' → '}
              {f.istQuantityX1000 != null ? f.istQuantityX1000 / 1000 : '—'}
              {f.deltaX1000 != null ? (
                <span className={f.deltaX1000 > 0 ? 'text-amber-600 ml-1' : 'text-red-600 ml-1'}>
                  ({fmtDelta(f.deltaX1000)})
                </span>
              ) : null}
            </span>
          )
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 130,
        cell: ({ getValue }) => <StatusBadge status={`finding_${getValue() as string}`} />,
      },
      {
        id: '__actions__',
        header: '',
        size: 50,
        enableSorting: false,
        cell: ({ row }) => (
          <Dropdown>
            <DropdownTrigger asChild>
              <IconButton
                icon={<Eye className="w-4 h-4" />}
                aria-label="Aktionen"
                variant="ghost"
                size="sm"
              />
            </DropdownTrigger>
            <DropdownContent align="end">
              <DropdownItem onClick={() => setFindingStatus(row.original, 'acknowledged')}>
                Zur Kenntnis genommen
              </DropdownItem>
              <DropdownItem onClick={() => setFindingStatus(row.original, 'resolved')}>
                Als erledigt markieren
              </DropdownItem>
              <DropdownItem onClick={() => setFindingStatus(row.original, 'ignored')}>
                Ignorieren
              </DropdownItem>
              {row.original.status !== 'open' ? (
                <DropdownItem onClick={() => setFindingStatus(row.original, 'open')}>
                  Wieder öffnen
                </DropdownItem>
              ) : null}
            </DropdownContent>
          </Dropdown>
        ),
      },
    ],
    [navigate],
  )

  const filterTabs: { value: FindingStatus | 'all'; label: string }[] = [
    { value: 'open', label: 'Offen' },
    { value: 'acknowledged', label: 'Zur Kenntnis' },
    { value: 'resolved', label: 'Erledigt' },
    { value: 'ignored', label: 'Ignoriert' },
    { value: 'all', label: 'Alle' },
  ]

  return (
    <div className="space-y-4">
      {/* KPI-Header */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <Card.Body>
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className={open > 0 ? 'w-4 h-4 text-amber-500' : 'w-4 h-4 text-gray-400'} />
              <p className="text-caption text-gray-500">Offene Findings</p>
            </div>
            <p className="text-h3 text-gray-900">{open}</p>
          </Card.Body>
        </Card>
        <Card>
          <Card.Body>
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className={critical > 0 ? 'w-4 h-4 text-red-500' : 'w-4 h-4 text-gray-400'} />
              <p className="text-caption text-gray-500">davon kritisch</p>
            </div>
            <p className="text-h3 text-gray-900">{critical}</p>
          </Card.Body>
        </Card>
        <Card>
          <Card.Body>
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle2 className="w-4 h-4 text-gray-400" />
              <p className="text-caption text-gray-500">Letzter Lauf</p>
            </div>
            <p className="text-small text-gray-900">
              {lastRun
                ? `${SOURCE_LABELS[lastRun.source]} · ${new Date(lastRun.startedAt).toLocaleString('de-DE', {
                    dateStyle: 'short',
                    timeStyle: 'short',
                  })}`
                : 'Noch kein Lauf'}
            </p>
          </Card.Body>
        </Card>
        <Card>
          <Card.Body className="flex flex-col justify-between h-full">
            <p className="text-caption text-gray-500 mb-2">Manueller Lauf</p>
            {availableSources.length === 0 ? (
              <p className="text-caption text-gray-400">Keine Reconciler aktiv</p>
            ) : (
              <Dropdown>
                <DropdownTrigger asChild>
                  <Button
                    variant="primary"
                    size="sm"
                    leadingIcon={<RefreshCw className="w-4 h-4" />}
                    loading={trigger.isPending}
                  >
                    Jetzt prüfen
                  </Button>
                </DropdownTrigger>
                <DropdownContent>
                  {availableSources.map((s) => (
                    <DropdownItem key={s} onClick={() => handleTrigger(s)}>
                      <Wand2 className="w-3.5 h-3.5 mr-2" />
                      {SOURCE_LABELS[s]} prüfen
                    </DropdownItem>
                  ))}
                </DropdownContent>
              </Dropdown>
            )}
          </Card.Body>
        </Card>
      </div>

      {/* Filter-Tabs */}
      <div className="flex items-center gap-2 flex-wrap">
        {filterTabs.map((t) => (
          <Button
            key={t.value}
            variant={statusFilter === t.value ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => setStatusFilter(t.value)}
          >
            {t.label}
          </Button>
        ))}
        {statusFilter !== 'open' ? (
          <Button
            variant="ghost"
            size="sm"
            leadingIcon={<EyeOff className="w-4 h-4" />}
            onClick={() => setStatusFilter('open')}
          >
            Nur offene
          </Button>
        ) : null}
      </div>

      <DataTable<ReconciliationFinding>
        data={findings.data ?? []}
        columns={columns}
        getRowId={(f) => f.id}
        loading={findings.isLoading}
        error={findings.error ? 'Findings konnten nicht geladen werden.' : null}
        onRetry={() => void findings.refetch()}
        searchPlaceholder="Findings suchen …"
        emptyState={
          <EmptyState
            title={statusFilter === 'open' ? 'Keine offenen Findings' : 'Keine Findings'}
            description={
              availableSources.length === 0
                ? 'Reconciler sind noch nicht konfiguriert. Erste Adapter werden in den nächsten Etappen angebunden.'
                : 'Lass einen Reconciler laufen, um Soll/Ist-Abweichungen zu erkennen.'
            }
          />
        }
      />
    </div>
  )
}
